import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { MillingJob } from 'models/milling';
import { useMutation } from '@apollo/client';
import { addMillingBatchTemplate } from 'apollo/templates/milling';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import StlMultiPreviewList from 'components/shared/stl-multi-preview-list/stl-multi-preview-list.component';
import { LoadingButton } from '@mui/lab';

type StartMillingProductionDialogProps = {
    jobs: MillingJob[];
    open: boolean;
    onProductionStarted?: () => void;
    onClose?: () => void;
};
const StartMillingProductionDialog: FC<StartMillingProductionDialogProps> = ({
    jobs,
    open,
    onClose,
    onProductionStarted,
}: StartMillingProductionDialogProps) => {
    const { t: translate } = useTranslation(['common', 'milling-jobs', 'orders']);
    const [batchNumber, setBatchNumber] = useState<string>('');
    const [disabledStartProductionButton, setDisabledStartProductionButton] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [addMillingBatch, { loading }] = useMutation(addMillingBatchTemplate, {
        onCompleted: () => {
            setDisabledStartProductionButton(false);
            if (onProductionStarted) {
                onProductionStarted();
            }
        },
        onError: (error) => {
            setDisabledStartProductionButton(false);
            showBoundary(error);
        },
    });

    const handleStartProductionClick = () => {
        setDisabledStartProductionButton(true);
        addMillingBatch({ variables: { millingJobIds: jobs.map((job) => job._id), batchNumber: batchNumber } });
    };

    useEffect(() => {
        setBatchNumber('');
    }, [open]);

    return (
        <Fragment>
            <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
                <DialogTitle>
                    <Box display="flex">
                        <Box flexGrow={1}>{translate('milling-jobs:MILLING_BATCH')}</Box>
                        <Box>
                            <Chip variant="outlined" label={translate('milling-jobs:NOT_STARTED')} color="info" />
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box mt={1} mb={3}>
                        <TextField
                            label={translate('orders:BATCH_NUMBER')}
                            sx={{ minWidth: 300 }}
                            value={batchNumber}
                            onChange={(event) => setBatchNumber(event.target.value)}
                        />
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('milling-jobs:JOB_GROUP')}</TableCell>
                                    <TableCell>{translate('milling-jobs:DEADLINE')}</TableCell>
                                    <TableCell>{translate('orders:COMMENT')}</TableCell>
                                    <TableCell>{translate('orders:ORDER')}</TableCell>
                                    <TableCell>{translate('orders:INTERNAL_ORDER_ID')}</TableCell>
                                    <TableCell>{translate('common:FILENAME')}</TableCell>
                                    <TableCell sx={{ width: 80 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobs.map((job) => (
                                    <TableRow key={job._id}>
                                        <TableCell>
                                            {job.jobType}: {job.material} {job.color}
                                        </TableCell>
                                        <TableCell>
                                            {job.deadline
                                                ? formatDate(job.deadline, 'date')
                                                : translate('milling-jobs:NO_DEADLINE')}
                                        </TableCell>
                                        {job.comment ? (
                                            <Tooltip title={job.comment} placement="top">
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: 200,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {job.comment}
                                                    </Typography>
                                                </TableCell>
                                            </Tooltip>
                                        ) : (
                                            <TableCell>-</TableCell>
                                        )}
                                        <TableCell>
                                            <Link
                                                style={{ cursor: 'pointer' }}
                                                target="_blank"
                                                href={`/order-details/${job.order._id}/main-view`}
                                            >
                                                {job.order.orderId}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            {job.order.internalOrderId
                                                ? job.order.internalOrderId
                                                : translate('orders:NO_INTERNAL_ORDER_ID')}
                                        </TableCell>
                                        <TableCell>{job.filename}</TableCell>
                                        <TableCell align="right">
                                            {job.stlFiles?.length > 0 && (
                                                <StlMultiPreviewList stlFiles={job.stlFiles} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{translate('common:CLOSE')}</Button>
                    <LoadingButton
                        disabled={disabledStartProductionButton}
                        variant="contained"
                        startIcon={<PlayCircleOutlineIcon />}
                        onClick={handleStartProductionClick}
                        loading={loading}
                    >
                        {translate('milling-jobs:START_PRODUCTION')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default StartMillingProductionDialog;
